if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');
        if (!this.elements.thumbnails) return;

        this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          mediaToSwitch
            .querySelector('button')
            .addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
        });
        this.desktopControls = this.elements.viewer.querySelector('[data-desktop-gallery-controls]');
        if (this.desktopControls) {
          this.desktopControls.addEventListener(
            'click',
            (event) => {
              const button = event.target.closest('button[name]');
              if (!button || !this.mql.matches) return;

              // Stop the mobile slider's built-in handler from acting on desktop controls.
              event.preventDefault();
              event.stopPropagation();
              this.changeDesktopMedia(button.name === 'next' ? 1 : -1);
            },
            true
          );
          this.updateDesktopControls();
        }

        this.enableDesktopThumbnailDrag();
        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
      }

      onSlideChanged(event) {
        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);

        // On mobile, swiping must also make that item active before loading video media.
        if (!this.mql.matches) {
          this.elements.viewer.querySelectorAll('.product__media-item[data-media-id]').forEach((element) => {
            element.classList.remove('is-active');
          });
          event.detail.currentElement.classList.add('is-active');
          this.playActiveMedia(event.detail.currentElement);
        }
      }

      setActiveMedia(mediaId, prepend) {
        const activeMedia =
          this.elements.viewer.querySelector(`.product__media-item[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('.product__media-item[data-media-id]');
        if (!activeMedia) {
          return;
        }
        this.elements.viewer.querySelectorAll('.product__media-item[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia?.classList?.add('is-active');

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            activeThumbnail.parentElement.firstChild !== activeThumbnail && activeThumbnail.parentElement.prepend(activeThumbnail);
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (!this.mql.matches) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
          const activeMediaRect = activeMedia.getBoundingClientRect();
          // Don't scroll if the image is already in view
          if (activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: 'smooth' });
        });
        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
        this.updateDesktopControls();
      }

      enableDesktopThumbnailDrag() {
        const track = this.elements.thumbnails?.querySelector('.thumbnail-list');
        if (!track) return;

        let pointerId = null;
        let startX = 0;
        let startScrollLeft = 0;
        let didDrag = false;
        let suppressClickUntil = 0;

        track.addEventListener('pointerdown', (event) => {
          if (!this.mql.matches || !event.isPrimary || (event.pointerType === 'mouse' && event.button !== 0)) return;
          pointerId = event.pointerId;
          startX = event.clientX;
          startScrollLeft = track.scrollLeft;
          didDrag = false;
        });

        track.addEventListener('pointermove', (event) => {
          if (!this.mql.matches || pointerId !== event.pointerId) return;
          const distance = event.clientX - startX;
          if (!didDrag && Math.abs(distance) < 6) return;

          if (!didDrag) {
            didDrag = true;
            track.setPointerCapture(event.pointerId);
            track.classList.add('is-dragging');
          }

          event.preventDefault();
          track.scrollLeft = startScrollLeft - distance;
        });

        const finishDrag = (event) => {
          if (pointerId !== event.pointerId) return;
          if (track.hasPointerCapture(event.pointerId)) track.releasePointerCapture(event.pointerId);
          if (didDrag) suppressClickUntil = Date.now() + 350;
          track.classList.remove('is-dragging');
          pointerId = null;
        };

        track.addEventListener('pointerup', finishDrag);
        track.addEventListener('pointercancel', finishDrag);
        track.addEventListener('dragstart', (event) => event.preventDefault());
        track.addEventListener('click', (event) => {
          if (Date.now() > suppressClickUntil) return;
          event.preventDefault();
          event.stopImmediatePropagation();
        }, true);
      }

      changeDesktopMedia(direction) {
        if (!this.mql.matches) return;

        const media = [...this.elements.viewer.querySelectorAll('.product__media-item[data-media-id]')];
        const activeIndex = Math.max(0, media.findIndex((element) => element.classList.contains('is-active')));
        const nextMedia = media[activeIndex + direction];
        if (!nextMedia) return;

        this.setActiveMedia(nextMedia.dataset.mediaId, false);
      }

      updateDesktopControls() {
        if (!this.desktopControls) return;

        const media = [...this.elements.viewer.querySelectorAll('.product__media-item[data-media-id]')];
        const activeIndex = media.findIndex((element) => element.classList.contains('is-active'));
        this.desktopControls.querySelector('[name="previous"]').disabled = activeIndex <= 0;
        this.desktopControls.querySelector('[name="next"]').disabled = activeIndex === -1 || activeIndex >= media.length - 1;
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (!deferredMedia) return;

        deferredMedia.loadContent(false);

        // Mobile gallery videos should start immediately, without persistent native controls.
        if (!this.mql.matches) {
          const videos = deferredMedia.querySelectorAll('video');
          const video = videos[videos.length - 1];
          if (video) {
            video.muted = true;
            video.defaultMuted = true;
            video.playsInline = true;
            video.controls = false;
            video.removeAttribute('controls');

            if (video.dataset.sofabayVideoControlsReady !== 'true') {
              video.dataset.sofabayVideoControlsReady = 'true';
              video.addEventListener('click', () => {
                video.controls = !video.controls;
                if (video.controls) {
                  video.setAttribute('controls', '');
                } else {
                  video.removeAttribute('controls');
                }
              });
            }

            const playback = video.play();
            if (playback) playback.catch(() => {});
          }
        }
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}
